import React, { memo, useCallback, useMemo } from "react";
import { useTheme } from "styled-components";
import { Radio } from "@swiggy-private/dls-web-core/dist/components/RadioGroup";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";

import {
    getOrderTypePreferenceOptionStyles,
    ON_PREMISE_SCREEN_SEAT_MAX_CHARS,
    ORDER_PREFERENCE,
    ORDER_TYPE_BOTTOM_SHEET_COPY,
    ORDER_TYPE_OPTIONS,
} from "./constants";
import {
    DeliverOptionGroup,
    FieldGrow,
    getOnPremiseTextFieldContainerStyle,
    InfoWrapper,
    RadioOptionSurface,
    RadioOptionsList,
    ScreenSeatRow,
    Wrapper,
} from "./OrderTypePreferenceOptions.style";
import type {
    OrderTypePreferenceOption,
    OrderTypePreferenceOptionBodyProps,
    OrderTypePreferenceOptionsProps,
} from "./types";

const OrderTypePreferenceOptionBody: React.FC<OrderTypePreferenceOptionBodyProps> = memo(
    ({ selected, heading, subHeading, onClick }) => {
        const { headingVariant, headingColor, subHeadingVariant, subHeadingColor } = useMemo(
            () => getOrderTypePreferenceOptionStyles(selected),
            [selected],
        );

        return (
            <RadioOptionSurface tabIndex={0} role="button" aria-hidden="true" onClick={onClick}>
                <InfoWrapper aria-hidden="true">
                    <Text variant={headingVariant} color={headingColor}>
                        {heading}
                    </Text>
                    <Text variant={subHeadingVariant} color={subHeadingColor}>
                        {subHeading}
                    </Text>
                </InfoWrapper>
            </RadioOptionSurface>
        );
    },
);

OrderTypePreferenceOptionBody.displayName = "OrderTypePreferenceOptionBody";

const OrderTypePreferenceOptions: React.FC<OrderTypePreferenceOptionsProps> = ({
    preference,
    audiNumber,
    seatNumber,
    screenInputRef,
    onPreferenceChange,
    onAudiChange,
    onSeatChange,
}) => {
    const theme = useTheme();
    const textFieldContainer = useMemo(() => getOnPremiseTextFieldContainerStyle(theme), [theme]);

    const renderOptionContent = useCallback(
        (item: OrderTypePreferenceOption, index: number) => (
            <OrderTypePreferenceOptionBody
                key={index}
                selected={preference === item.option}
                heading={item.label}
                subHeading={item.description}
                onClick={() => onPreferenceChange(item.option)}
            />
        ),
        [preference, onPreferenceChange],
    );

    const renderPreferenceRadio = useCallback(
        (item: OrderTypePreferenceOption, index: number) => (
            <Wrapper key={item.option}>
                <Radio
                    label=""
                    id={item.id}
                    value={item.option}
                    orientation="ltr"
                    Comp={() => renderOptionContent(item, index)}
                    onChange={() => onPreferenceChange(item.option)}
                    name="on-premise-order-type"
                    checked={preference === item.option}
                    aria-hidden="true"
                    color={item.color}
                />
            </Wrapper>
        ),
        [preference, renderOptionContent, onPreferenceChange],
    );

    const deliverOption = ORDER_TYPE_OPTIONS[0];
    const pickupOption = ORDER_TYPE_OPTIONS[1];
    const showSeatFields = preference === ORDER_PREFERENCE.DELIVER_TO_SEAT;

    return (
        <RadioOptionsList>
            <DeliverOptionGroup>
                {renderPreferenceRadio(deliverOption, 0)}
                {showSeatFields ? (
                    <ScreenSeatRow>
                        <FieldGrow>
                            <TextField
                                ref={screenInputRef}
                                textFieldDetails={{
                                    value: audiNumber,
                                    label: ORDER_TYPE_BOTTOM_SHEET_COPY.SCREEN_LABEL,
                                    hintText: ORDER_TYPE_BOTTOM_SHEET_COPY.SCREEN_PLACEHOLDER,
                                    name: "on-premise-screen",
                                    type: TextFieldTypes.TEXT,
                                    onChange: onAudiChange,
                                    inputStyle: textFieldContainer,
                                    attrs: {
                                        inputMode: "text",
                                        autoComplete: "off",
                                        maxLength: ON_PREMISE_SCREEN_SEAT_MAX_CHARS,
                                    },
                                }}
                            />
                        </FieldGrow>
                        <FieldGrow>
                            <TextField
                                textFieldDetails={{
                                    value: seatNumber,
                                    label: ORDER_TYPE_BOTTOM_SHEET_COPY.SEAT_LABEL,
                                    hintText: ORDER_TYPE_BOTTOM_SHEET_COPY.SEAT_PLACEHOLDER,
                                    name: "on-premise-seat",
                                    type: TextFieldTypes.TEXT,
                                    onChange: onSeatChange,
                                    inputStyle: textFieldContainer,
                                    attrs: {
                                        autoComplete: "off",
                                        inputMode: "text",
                                        maxLength: ON_PREMISE_SCREEN_SEAT_MAX_CHARS,
                                    },
                                }}
                            />
                        </FieldGrow>
                    </ScreenSeatRow>
                ) : null}
            </DeliverOptionGroup>
            {renderPreferenceRadio(pickupOption, 1)}
        </RadioOptionsList>
    );
};

export default memo(OrderTypePreferenceOptions);
